import { useCallback } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { useCompleteTask } from '../useCompleteTask';
import type { TaskType } from '../useCompleteTask/constants';
import { useNextStepsExperiment } from '../useNextStepsExperiment';
import { usePetitionAiGuidanceExperiment } from '../usePetitionAiGuidanceExperiment';
import { useUpdatePersonalizedPetitionTask } from '../useUpdatePersonalizedPetitionTask';
import { UPDATE_TASK_ERROR_MAP } from '../useUpdatePersonalizedPetitionTask/constants';

const PERSONALIZED_TASKS_COMPLETION_EVENT = 'petition_manage_tasks_completed';

export function useAutoCheckOffTask() {
	const {
		data: { isEnabled: isNextStepsEnabled },
	} = useNextStepsExperiment();
	const {
		data: { isEnabled: isPetitionAiGuidanceEnabled },
	} = usePetitionAiGuidanceExperiment();
	const {
		data: {
			petition: { id: petitionId, activeTasks, personalizedTasks },
		},
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const track = useTracking();
	const { mutate: completeTask } = useCompleteTask({
		completionType: 'automatic',
		version: 'new_12_2025',
	});
	const { mutate: updatePersonalizedTask } = useUpdatePersonalizedPetitionTask({
		onError: (error) => {
			if (error === UPDATE_TASK_ERROR_MAP.UNAUTHENTICATED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			}
		},
	});

	const handleAutoCheckOffTask = useCallback(
		async (taskId: TaskType) => {
			if (isPetitionAiGuidanceEnabled) {
				if (personalizedTasks.__typename !== 'PersonalizedTasksSuccess') {
					return;
				}

				const matchingTasks = personalizedTasks.tasks.filter(
					(task) => task.featureKey === taskId && task.status === 'ACTIVE',
				);

				if (matchingTasks.length === 0) {
					return;
				}

				const results = await Promise.all(
					matchingTasks.map(async (task) => {
						const result = await updatePersonalizedTask({
							petitionId,
							personalizedPetitionTaskId: task.id,
							status: 'COMPLETED',
						});

						if (result.status === 'loaded') {
							void track(PERSONALIZED_TASKS_COMPLETION_EVENT, {
								task: 'mark_as_done',
								task_name: task.title,
								completed: 'automatic',
								version: 'new_ai_03_2026',
							});
						}

						return result;
					}),
				);

				if (results.every((result) => result.status === 'loaded')) {
					refreshData();
				}

				return;
			}

			if (isNextStepsEnabled) {
				if (activeTasks.__typename !== 'PetitionTasksConnection') {
					return;
				}
				const task = activeTasks.nodes.find((t) => t.name === taskId);
				if (task) {
					await completeTask(task.id, task.name);
					refreshData();
				}
			}
		},
		[
			isPetitionAiGuidanceEnabled,
			personalizedTasks,
			updatePersonalizedTask,
			petitionId,
			track,
			refreshData,
			isNextStepsEnabled,
			completeTask,
			activeTasks,
		],
	);

	return handleAutoCheckOffTask;
}
